import type {
  EssentialsAppAdvertisingComponent,
  EssentialsBlocksComponent,
  EssentialsCarouselComponent,
  EssentialsDividerComponent,
  EssentialsFocusComponent,
  EssentialsGalleryComponent,
  EssentialsHeroComponent,
  EssentialsImageBoxSliderComponent,
  EssentialsInstallStepsComponent,
  EssentialsListComponent,
  EssentialsListHorizontalComponent,
  EssentialsMoreComponent,
  EssentialsOurVisionComponent,
  EssentialsRightImageComponent,
  EssentialsSimpleTextComponent,
  EssentialsVideoComponent,
  SectionsAppAdvertisingComponent,
  SectionsBlocksComponent,
  SectionsCarouselComponent,
  SectionsDividerComponent,
  SectionsFocusComponent,
  SectionsGalleryComponent,
  SectionsHeroComponent,
  SectionsImageBoxSliderComponent,
  SectionsInstallStepsComponent,
  SectionsListComponent,
  SectionsListHorizontalComponent,
  SectionsMoreComponent,
  SectionsOurVisionComponent,
  SectionsRightImageComponent,
  SectionsSimpleTextComponent,
  SectionsVideoComponent,
} from '@twisto/api-cms'

import {
  AppAdvertising,
  Blocks,
  Carousel,
  Divider,
  Focus,
  Gallery,
  Hero,
  ImageBoxSlider,
  InstallSteps,
  List,
  ListHorizontal,
  More,
  OurVision,
  RightImage,
  SimpleText,
  Video,
} from '../sections'

const COMPONENTS_TYPE = {
  ESSENTIALS_HERO: 'essentials.hero',
  ESSENTIALS_SIMPLE_TEXT: 'essentials.simple-text',
  ESSENTIALS_BLOCKS: 'essentials.blocks',
  ESSENTIALS_RIGHT_IMAGE: 'essentials.right-image',
  ESSENTIALS_APP_ADVERTISING: 'essentials.app-advertising',
  ESSENTIALS_INSTALL_STEPS: 'essentials.install-steps',
  ESSENTIALS_FOCUS: 'essentials.focus',
  ESSENTIALS_CAROUSEL: 'essentials.carousel',
  ESSENTIALS_IMAGE_BOX_SLIDER: 'essentials.image-box-slider',
  ESSENTIALS_LIST: 'essentials.list',
  ESSENTIALS_LIST_HORIZONTAL: 'essentials.list-horizontal',
  ESSENTIALS_OUR_VISION: 'essentials.our-vision',
  ESSENTIALS_GALLERY: 'essentials.gallery',
  ESSENTIALS_VIDEO: 'essentials.video',
  ESSENTIALS_MORE: 'essentials.more',
  ESSENTIALS_DIVIDER: 'essentials.divider',
} as const

export type EssentialType =
  | (EssentialsHeroComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_HERO']
    })
  | (EssentialsSimpleTextComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_SIMPLE_TEXT']
    })
  | (EssentialsBlocksComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_BLOCKS']
    })
  | (EssentialsRightImageComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_RIGHT_IMAGE']
    })
  | (EssentialsAppAdvertisingComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_APP_ADVERTISING']
    })
  | (EssentialsInstallStepsComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_INSTALL_STEPS']
    })
  | (EssentialsFocusComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_FOCUS']
    })
  | (EssentialsCarouselComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_CAROUSEL']
    })
  | (EssentialsImageBoxSliderComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_IMAGE_BOX_SLIDER']
    })
  | (EssentialsListComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_LIST']
    })
  | (EssentialsListHorizontalComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_LIST_HORIZONTAL']
    })
  | (EssentialsOurVisionComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_OUR_VISION']
    })
  | (EssentialsGalleryComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_GALLERY']
    })
  | (EssentialsVideoComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_VIDEO']
    })
  | (EssentialsMoreComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_MORE']
    })
  | (EssentialsDividerComponent & {
      __component: (typeof COMPONENTS_TYPE)['ESSENTIALS_DIVIDER']
    })

type EssentialsProps = {
  essentials?: EssentialType[]
}

export const Essential = ({
  essentialData,
}: {
  essentialData: EssentialType
}) => {
  switch (essentialData.__component) {
    case 'essentials.hero':
      return <Hero {...(essentialData as unknown as SectionsHeroComponent)} />
    case 'essentials.simple-text':
      return (
        <SimpleText
          {...(essentialData as unknown as SectionsSimpleTextComponent)}
        />
      )
    case 'essentials.blocks':
      return (
        <Blocks {...(essentialData as unknown as SectionsBlocksComponent)} />
      )
    case 'essentials.right-image':
      return (
        <RightImage
          {...(essentialData as unknown as SectionsRightImageComponent)}
        />
      )
    case 'essentials.app-advertising':
      return (
        <AppAdvertising
          {...(essentialData as unknown as SectionsAppAdvertisingComponent)}
        />
      )
    case 'essentials.install-steps':
      return (
        <InstallSteps
          {...(essentialData as unknown as SectionsInstallStepsComponent)}
        />
      )
    case 'essentials.focus':
      return <Focus {...(essentialData as unknown as SectionsFocusComponent)} />
    case 'essentials.carousel':
      return (
        <Carousel
          {...(essentialData as unknown as SectionsCarouselComponent)}
        />
      )
    case 'essentials.image-box-slider':
      return (
        <ImageBoxSlider
          {...(essentialData as unknown as SectionsImageBoxSliderComponent)}
        />
      )
    case 'essentials.list':
      return <List {...(essentialData as unknown as SectionsListComponent)} />
    case 'essentials.list-horizontal':
      return (
        <ListHorizontal
          {...(essentialData as unknown as SectionsListHorizontalComponent)}
        />
      )
    case 'essentials.our-vision':
      return (
        <OurVision
          {...(essentialData as unknown as SectionsOurVisionComponent)}
        />
      )
    case 'essentials.gallery':
      return (
        <Gallery {...(essentialData as unknown as SectionsGalleryComponent)} />
      )
    case 'essentials.video':
      return <Video {...(essentialData as unknown as SectionsVideoComponent)} />
    case 'essentials.more':
      return <More {...(essentialData as unknown as SectionsMoreComponent)} />
    case 'essentials.divider':
      return (
        <Divider {...(essentialData as unknown as SectionsDividerComponent)} />
      )
    default:
      return null
  }
}

export const Essentials = ({ essentials }: EssentialsProps) => (
  <>
    {essentials?.map((essential, index) => (
      <Essential
        key={`${essential.__component}${index}`}
        essentialData={essential}
      />
    ))}
  </>
)
